import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button, Container, Input, Loader, Table, Alert } from '@itds/elements/index.js';
import H1 from '@brokerage/components/H1';
import Avatar from '@brokerage/components/Avatar';
import { debounce } from 'lodash';
import DOMPurify from 'dompurify';
import NewDrawer from './New';
import { useDispatch, useSelector } from 'react-redux';
import {
  fetchMembers,
  setCurrentPage,
  setSearchQuery,
  clearCache
} from '@redux/brokerageSlices/memberSlice';

const List = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { members, pagination, loading, error, currentPage, searchQuery } = useSelector(
    state => state.members
  );

  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [showNew, toggleNewDrawer] = useState(false);

  useEffect(() => {
    dispatch(fetchMembers({ page: currentPage, search: searchQuery }));
  }, [dispatch, currentPage, searchQuery]);

  const debouncedSetSearchTerm = useMemo(
    () =>
      debounce(value => {
        dispatch(setSearchQuery(value));
      }, 500),
    [dispatch]
  );

  const handleSearchChange = e => {
    let value = e.target.value.trimStart();

    value = DOMPurify.sanitize(value);

    setLocalSearch(value);

    debouncedSetSearchTerm(value);
  };

  const handleSearchKeyDown = e => {
    if (e.keyCode === 13) {
      debouncedSetSearchTerm.cancel();
      dispatch(setSearchQuery(localSearch));
      dispatch(setCurrentPage(1));
      dispatch(clearCache());
    }
  };

  const handlePageChange = newPage => {
    window.scrollTo(0, 0);
    dispatch(setCurrentPage(newPage));
    dispatch(fetchMembers({ page: newPage, search: searchQuery }));
  };

  const handleNewMember = () => {
    toggleNewDrawer(false);
    dispatch(fetchMembers({ page: currentPage, search: searchQuery }));
  };

  return (
    <Container>
      {error && <Alert type='error'>{error}</Alert>}

      <NewDrawer onClose={() => toggleNewDrawer(false)} onSave={handleNewMember} show={showNew} />

      <H1>Members</H1>

      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 30 }}>
        <Input
          onChange={handleSearchChange}
          onKeyDown={handleSearchKeyDown}
          placeholder='Search by name or email...'
          style={{ width: 300 }}
          value={localSearch}
        />
        <div style={{ marginLeft: 'auto' }}>
          <Button onClick={() => toggleNewDrawer(true)}>Add Member</Button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : (
        <Table
          apiPagination={{ ...pagination, current_page: currentPage, onClick: handlePageChange }}
          columns={[
            {
              id: 'image',
              label: '',
              width: 50,
              render: member => <Avatar image={member.image.url} />,
              styles: { padding: '30px 15px', verticalAlign: 'top' }
            },
            {
              id: 'name',
              label: 'Name',
              styles: { padding: '30px 15px', verticalAlign: 'top' },
              render: member => `${member.first_name} ${member.last_name}`
            },
            {
              id: 'email',
              label: 'Email',
              styles: { padding: '30px 15px', verticalAlign: 'top' }
            },
            {
              id: 'role',
              label: 'Agencies',
              styles: { padding: '30px 15px', verticalAlign: 'top' },
              render: member =>
                member.agencies.map(a => (
                  <Link
                    key={a.id}
                    onClick={e => e.stopPropagation()}
                    style={{ display: 'block' }}
                    to={`/agencies/${a.id}/general`}
                  >
                    {a.name}
                  </Link>
                ))
            }
          ]}
          data={members}
          onRowClick={member => navigate(`/members/${member.id}/general`)}
        />
      )}
    </Container>
  );
};

export default List;
